import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import LoadingSpinner from "../../components/loader/Loader";
import { useResetPasswordMutation } from "../../features/api/authApiSlice";
import { validatePasswordStrength } from "../../util/validatePasswordStrength";

import { useRecaptcha, executeRecaptcha } from "../../hooks/useRecaptcha";

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const ResetPassword = () => {
  const { resetToken } = useParams();
  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const passwordCriteria = validatePasswordStrength(password);

  useRecaptcha(SITE_KEY, ["/resetpassword"]); // ✅ only show badge on /resetpassword

  const handleSubmit = async (e) => {
    e.preventDefault();

    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&()[\]{}])[A-Za-z\d@$!%*#?&()[\]{}]{8,}$/;

    if (!strongPasswordRegex.test(password)) {
      toast.error(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character."
      );
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      const token = await executeRecaptcha(SITE_KEY, "resetpassword");

      await resetPassword({
        resetToken,
        password,
        recaptchaToken: token,
      }).unwrap();
      toast.success("Password reset successful!");
      navigate("/login");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to reset password");
    }
  };

  return (
    <div className="auth-form">
      <div className="main">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="auth-from-container">
            <h2>Reset Password</h2>
            <p>Enter a new password to recover your account.</p>
            <form onSubmit={handleSubmit}>
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <ul className="password-rules">
                <li className={passwordCriteria.length ? "valid" : ""}>
                  Minimum 8 characters
                </li>
                <li className={passwordCriteria.upper ? "valid" : ""}>
                  At least 1 uppercase letter
                </li>
                <li className={passwordCriteria.lower ? "valid" : ""}>
                  At least 1 lowercase letter
                </li>
                <li className={passwordCriteria.number ? "valid" : ""}>
                  At least 1 number
                </li>
                <li className={passwordCriteria.special ? "valid" : ""}>
                  At least 1 special character
                </li>
              </ul>

              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <button type="submit" disabled={isLoading}>
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
